import type { ButtonHTMLAttributes } from 'react'

/**
 * Interactive-state classes every button-like element should have, centralized here so any
 * button in the app — whether it uses this component's presets or fully custom styling via the
 * `"unstyled"` variant — reliably shows a hand cursor and a correct disabled state.
 *
 * Deliberately excludes layout/color/focus-ring classes so it never conflicts with a consumer's
 * own styling; combine it with your own classes rather than the other way around.
 */
export const BUTTON_INTERACTIVE_CLASSES =
  'cursor-pointer disabled:pointer-events-none disabled:cursor-not-allowed'

/** Visual style variant for the Button. `"unstyled"` applies no colors/layout — combine with `className` for full control while still getting {@link BUTTON_INTERACTIVE_CLASSES}. */
type Variant = 'primary' | 'secondary' | 'ghost' | 'unstyled'

/** Size preset for the Button. Ignored when `variant` is `"unstyled"`. */
type Size = 'sm' | 'md' | 'lg'

/** Props for the Button component. */
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant. Defaults to "primary". */
  variant?: Variant
  /** Size preset. Defaults to "md". */
  size?: Size
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-blue-500',
  secondary:
    'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-400',
  ghost: 'text-gray-700 hover:bg-gray-100 focus-visible:ring-gray-400',
  unstyled: '',
}

const sizeClasses: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

/** Structural classes applied to every preset variant; skipped entirely for `"unstyled"`. */
const LAYOUT_CLASSES =
  'inline-flex items-center justify-center rounded-md font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-50'

/**
 * Reusable button component with variant and size presets.
 *
 * Use `variant="unstyled"` to keep this component's centralized interactive behavior (hand
 * cursor, disabled state) while fully controlling appearance via `className`/`style` — useful
 * for one-off branded buttons that can't use the built-in presets.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: ButtonProps) {
  const isUnstyled = variant === 'unstyled'
  const classes = [
    BUTTON_INTERACTIVE_CLASSES,
    !isUnstyled && LAYOUT_CLASSES,
    !isUnstyled && sizeClasses[size],
    variantClasses[variant],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return <button className={classes} {...props} />
}
